import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import type { Question, Section } from '../../config/types';
import type {
  ApprovalRecord,
  FetchFormResponse,
  ScsReviewQuarter,
  ScsReviewSaveResult,
} from '../../service/types';
import { extractConditionalQuestions } from '../../utils/config-utils';
import { ApiError } from '../../utils/api-error';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import PageTemplate from '../PageTemplate/PageTemplate';
import BulkTableModal from '../form/bulk-table/BulkTableModal';
import ScsReviewTabs from './ScsReviewTabs';
import ScsStaffForm, { type StaffSummarySection } from './ScsStaffForm';
import useReviewLeaveWarning from './useReviewLeaveWarning';

type RenderQuestion = (
  question: Question,
  methods: UseFormReturn<Record<string, unknown>>,
) => React.ReactNode;
type Save = (payload: Record<string, unknown>) => Promise<ScsReviewSaveResult>;

function reviewQuestion(question: Question): Question {
  return {
    ...question,
    ...(['textarea', 'textInput'].includes(question.questionType)
      ? { rules: { reservedCharacters: '', ...question.rules } }
      : {}),
    ...(Array.isArray(question.options)
      ? {
          options: question.options.map((option) =>
            typeof option === 'object' && 'questions' in option
              ? { ...option, questions: option.questions?.map(reviewQuestion) }
              : option,
          ),
        }
      : {}),
  };
}

function QuarterReview({
  context,
  data,
  questions,
  active,
  staffForm,
  renderQuestion,
  onSave,
  onSaved,
  onDirty,
  onLoadSaved,
}: {
  context: ScsReviewQuarter;
  data: FetchFormResponse;
  questions: Question[];
  active: boolean;
  staffForm: React.ReactNode;
  renderQuestion: RenderQuestion;
  onSave: Save;
  onSaved: (quarter: string, result: ScsReviewSaveResult) => void;
  onDirty: (quarter: string, dirty: boolean) => void;
  onLoadSaved: () => Promise<FetchFormResponse>;
}) {
  const { quarter, year } = context;
  const [revision, setRevision] = useState(context.draft?.revision || context.revision);
  const [completed, setCompleted] = useState(context.managerComplete);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [instructionsError, setInstructionsError] = useState('');
  const staffName = String(data.answers?.['person-details-scs-name'] || 'the staff member');
  const [comparison, setComparison] = useState<{
    context: ScsReviewQuarter;
    approval?: ApprovalRecord;
  }>();
  const [confirmedEarly, setConfirmedEarly] = useState(context.staffComplete);
  const saved = Object.values(data.approvals || {}).find(
    (entry) => entry.quarter === quarter && entry.year === year && entry.status !== 'Rejected',
  );
  const [defaults] = useState(() => {
    const source = context.draft?.answers || saved?.answers || data.answers || {};
    const initial: Record<string, unknown> = {};
    extractConditionalQuestions(questions).forEach(({ name }) => {
      initial[name] = source[name] ?? '';
    });
    questions
      .filter((question) => question.questionType === 'reportPeriod')
      .forEach(({ name }) => {
        initial[name] = { quarter, year };
      });
    initial.approvalComment = context.draft?.comments ?? saved?.comments ?? '';
    return initial;
  });
  const methods = useForm<Record<string, unknown>>({
    defaultValues: defaults,
    shouldUnregister: false,
  });
  const { isDirty } = methods.formState;
  useEffect(() => {
    onDirty(quarter, isDirty);
  }, [isDirty, onDirty, quarter]);

  const save = async (draft: boolean, values: Record<string, unknown>, reject = false) => {
    if (
      !draft &&
      !reject &&
      completed &&
      !window.confirm(`Replace the saved ${quarter} manager review with these answers?`)
    )
      return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const answers = Object.fromEntries(
        extractConditionalQuestions(questions).map(({ name }) => [name, values[name] ?? '']),
      );
      const result = await onSave({
        action: draft ? 'save_manager_draft' : reject ? 'reject' : 'approve',
        approvalQuarter: quarter,
        approvalYear: year,
        approvalAnswers: answers,
        comments: values.approvalComment || '',
        reviewRevision: revision,
      });
      setRevision(result.revision);
      if (!draft) setCompleted(!reject);
      if (reject) {
        setShowReturn(false);
        setInstructions('');
      }
      methods.reset(values);
      onSaved(quarter, result);
      setMessage(
        draft
          ? `${quarter} draft saved.`
          : reject
            ? `${quarter} returned to staff.`
            : `${quarter} review saved.`,
      );
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.detail || reason.message
          : reason instanceof Error
            ? reason.message
            : 'Unable to save this review. Your input has been kept.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      role="tabpanel"
      id={`review-panel-${quarter}`}
      aria-labelledby={`review-tab-${quarter}`}
      hidden={!active}
    >
      <h2 className="govuk-heading-l">{quarter} manager review</h2>
      <p>
        <strong className={`govuk-tag ${completed ? 'govuk-tag--green' : 'govuk-tag--blue'}`}>
          {completed ? 'Review complete' : 'In progress'}
        </strong>
      </p>
      <p className="govuk-body">Reporting year: {year}</p>
      {!context.staffComplete && (
        <div className="govuk-inset-text">
          <strong>Staff section incomplete</strong>
          <p className="govuk-body">
            The staff member has not completed their {quarter} section. You can complete your review
            now; their input will still be required.
          </p>
          {!confirmedEarly && (
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={() => setConfirmedEarly(true)}
            >
              Continue with manager review
            </button>
          )}
        </div>
      )}
      {active && staffForm}
      <p className="govuk-body">
        {!confirmedEarly
          ? `${staffName} has not yet completed and submitted their ${quarter} staff performance section for approval. You can return it once they have submitted it.`
          : `Return the form to ${staffName} if they need to make changes. You can add instructions before sending it back.`}
      </p>
      <button
        type="button"
        className="govuk-button govuk-button--secondary"
        disabled={saving || !confirmedEarly}
        onClick={() => {
          setInstructionsError('');
          setError('');
          setShowReturn(true);
        }}
      >
        Return {quarter} to staff
      </button>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit((values) => save(false, values))}>
          <fieldset disabled={saving || !confirmedEarly} className="govuk-fieldset">
            <legend className="govuk-visually-hidden">{quarter} manager assessment</legend>
            <ErrorSummary errors={methods.formState.errors} />
            {questions.map((question) => renderQuestion(question, methods))}
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor={`review-notes-${quarter}`}>
                Notes (optional)
              </label>
              {methods.formState.errors.approvalComment && (
                <p className="govuk-error-message">
                  {String(methods.formState.errors.approvalComment.message)}
                </p>
              )}
              <textarea
                id={`review-notes-${quarter}`}
                className="govuk-textarea"
                rows={4}
                {...methods.register('approvalComment')}
              />
            </div>
            <div className="govuk-button-group">
              <button type="submit" className="govuk-button">
                {completed ? `Save changes to ${quarter} review` : `Complete ${quarter} review`}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                onClick={() => save(true, methods.getValues())}
              >
                Save draft and complete later
              </button>
            </div>
          </fieldset>
        </form>
      </FormProvider>
      {showReturn && (
        <BulkTableModal
          title={`Return ${quarter} to staff`}
          onClose={() => {
            if (!saving) setShowReturn(false);
          }}
          width="min(100%, 700px)"
        >
          <p className="govuk-body" id={`return-description-${quarter}`}>
            {staffName} will receive an email asking them to update their Performance Form. Enter
            the changes they need to make below.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (saving) return;
              if (!instructions.trim()) {
                setInstructionsError('Enter the changes they need to make.');
                document.getElementById(`return-instructions-${quarter}`)?.focus();
                return;
              }
              void save(
                false,
                { ...methods.getValues(), approvalComment: instructions.trim() },
                true,
              );
            }}
          >
            <div
              className={`govuk-form-group ${instructionsError ? 'govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor={`return-instructions-${quarter}`}>
                Instructions for {staffName}
              </label>
              {instructionsError && (
                <p className="govuk-error-message" role="alert" id={`return-error-${quarter}`}>
                  {instructionsError}
                </p>
              )}
              <textarea
                id={`return-instructions-${quarter}`}
                className={`govuk-textarea ${instructionsError ? 'govuk-textarea--error' : ''}`}
                rows={5}
                value={instructions}
                disabled={saving}
                aria-invalid={!!instructionsError}
                aria-describedby={`return-description-${quarter}${instructionsError ? ` return-error-${quarter}` : ''}`}
                onChange={(event) => {
                  setInstructions(event.target.value);
                  setInstructionsError('');
                }}
              />
            </div>
            {error && (
              <p className="govuk-error-message" role="alert">
                {error}
              </p>
            )}
            <div className="govuk-button-group">
              <button type="submit" className="govuk-button" disabled={saving}>
                {saving ? 'Sending...' : 'Send email'}
              </button>
              <button
                type="button"
                className="govuk-button"
                disabled={saving}
                onClick={() => setShowReturn(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </BulkTableModal>
      )}
      <p role="status" className="govuk-body">
        {saving ? 'Saving...' : message}
      </p>
      {error && !showReturn && (
        <div role="alert" className="govuk-inset-text">
          <p className="govuk-body">{error}</p>
          <button
            type="button"
            className="govuk-button govuk-button--secondary"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                const latest = await onLoadSaved();
                const next = latest.scsReview?.quarters?.[quarter];
                if (!next?.available)
                  throw new Error(
                    'This quarter is no longer available for review. Your input has been kept.',
                  );
                setComparison({
                  context: next,
                  approval: Object.values(latest.approvals || {}).find(
                    (entry) => entry.quarter === quarter && entry.year === year,
                  ),
                });
              } catch (reason) {
                setError(
                  reason instanceof Error ? reason.message : 'Unable to load the saved review.',
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            Load saved review for comparison
          </button>
        </div>
      )}
      {comparison && (
        <div className="govuk-inset-text">
          <h3 className="govuk-heading-m">Latest saved {quarter} review</h3>
          <dl className="govuk-summary-list">
            {extractConditionalQuestions(questions)
              .filter((question) => comparison.approval?.answers?.[question.name])
              .map((question) => (
                <div className="govuk-summary-list__row" key={question.name}>
                  <dt className="govuk-summary-list__key">{question.title}</dt>
                  <dd className="govuk-summary-list__value" style={{ whiteSpace: 'pre-wrap' }}>
                    {String(comparison.approval?.answers?.[question.name])}
                  </dd>
                </div>
              ))}
          </dl>
          <button
            type="button"
            className="govuk-button govuk-button--secondary"
            onClick={() => {
              setRevision(comparison.context.revision);
              setCompleted(comparison.context.managerComplete);
              setComparison(undefined);
              setError('');
              setMessage(
                'Your input is unchanged. Check it against the saved review before saving again.',
              );
            }}
          >
            Keep my input and use this saved version
          </button>
        </div>
      )}
    </section>
  );
}

export default function ScsManagerReview({
  data,
  sections,
  visible,
  renderQuestion,
  onSave,
  onLoadSaved,
}: {
  data: FetchFormResponse;
  sections: Section[];
  visible: StaffSummarySection[];
  renderQuestion: RenderQuestion;
  onSave: Save;
  onLoadSaved: () => Promise<FetchFormResponse>;
}) {
  const review = data.scsReview!;
  const [selected, setSelected] = useState(review.quarter);
  const [quarters, setQuarters] = useState(review.quarters!);
  const [approvals, setApprovals] = useState(data.approvals || {});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const onDirty = React.useCallback(
    (quarter: string, value: boolean) =>
      setDirty((previous) =>
        previous[quarter] === value ? previous : { ...previous, [quarter]: value },
      ),
    [],
  );
  useReviewLeaveWarning(Object.values(dirty).some(Boolean));
  const staffForm = (
    <ScsStaffForm
      data={data}
      sections={sections}
      visible={visible}
      approvals={approvals}
      year={review.year}
      selectedQuarter={selected}
    />
  );
  return (
    <PageTemplate title="Manager review" fullWidth>
      <p className="govuk-body">
        Reviewing{' '}
        <strong>{String(data.answers?.['person-details-scs-name'] || 'staff member')}</strong>
      </p>
      <ScsReviewTabs
        quarters={quarters}
        selected={selected}
        current={review.quarter}
        dirty={dirty}
        onSelect={setSelected}
      />
      {Object.values(quarters)
        .filter((entry) => entry.available)
        .map((entry) => {
          const page = sections
            .flatMap((section) => section.subsections.flatMap((subsection) => subsection.pages))
            .find((item) => item.name === `approval-${entry.quarter.toLowerCase()}`);
          if (!page || !('questions' in page)) return null;
          return (
            <QuarterReview
              key={entry.quarter}
              context={entry}
              data={data}
              questions={(page.questions || []).map(reviewQuestion)}
              active={selected === entry.quarter}
              staffForm={staffForm}
              renderQuestion={renderQuestion}
              onSave={onSave}
              onLoadSaved={async () => {
                const latest = await onLoadSaved();
                setApprovals(latest.approvals || {});
                setQuarters((previous) =>
                  Object.fromEntries(
                    Object.entries(previous).map(([quarter, entry]) => {
                      const saved = latest.scsReview?.quarters?.[quarter];
                      return [
                        quarter,
                        saved
                          ? {
                              ...entry,
                              managerComplete: saved.managerComplete,
                              completedAt: saved.completedAt,
                            }
                          : entry,
                      ];
                    }),
                  ),
                );
                return latest;
              }}
              onDirty={onDirty}
              onSaved={(quarter, result) => {
                if (result.approval) {
                  setApprovals((previous) => ({
                    ...previous,
                    [`${quarter}-${review.year}`]: result.approval!,
                  }));
                  setQuarters((previous) => ({
                    ...previous,
                    [quarter]: {
                      ...previous[quarter],
                      managerComplete: result.approval!.status !== 'Rejected',
                      completedAt: result.approval!.approved_at || '',
                      revision: result.revision,
                    },
                  }));
                }
              }}
            />
          );
        })}
    </PageTemplate>
  );
}
